import { useUser } from 'contexts/UserContext';
import { useDebounceCallback } from 'custom_hooks/useDebounceCallback';
import { saveWorkbenchStateToBrowserStorage } from 'pages/workbench/shared';
import { useDefaultTimeRange } from 'pages/workbench/shared/useDefaultTimeRange';
import { useWorkbenchValues, WorkbenchContext } from 'pages/workbench/state';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isZoomedIn } from 'shared/chart/shared/data';
import { useZustandStore } from 'shared/zustand_context/zustand_context';
import useInterval from 'use-interval';
import { extractTrendUrlParams, TrendQueryParams } from 'utils/RouteHelper';
import { useQueryParams } from 'utils/useQueryParams';

import {
    _WorkbenchStateInternal,
    extractWorkbenchStateInitializerMethods,
    WorkbenchStateInitializerMethods,
} from './types';
import { useLoadWorkbenchStateFromParams } from './useLoadWorkbenchStateFromParams';

const BrowserStorageDebounceMs = 1000;
const AutoScrollUpdateMs = 30 * 1000;

/**
 * A function that initializes workbench state
 */
type StateInitializerFn = (
    stateInitializers: WorkbenchStateInitializerMethods
) => Promise<void> | void;

/**
 * A function that will not be executed until isLoading is true
 */
type LoadFunctionDefinition = {
    isLoading: boolean;
    fn: StateInitializerFn;
};

export type WorkbenchInitializationOptions = Readonly<{
    systemId: string;
    locationId: string;
    saveUpdatesToBrowserStorage: boolean;
    updateStateFromUrl: boolean;
    /**
     * Optional list of functions that will be executed after the first phase of
     * workbench of initialization has happened (i.e. after loading from local
     * storage / url / session, etc.)
     *
     * The Workbench's `isInitialized` will be set to `true` only after these
     * `additionalLoadActions` have completed.
     */
    additionalLoadActions?: (LoadFunctionDefinition | StateInitializerFn)[];
}>;

/**
 * Initializes the workbench for the supplied system.  Loads workbench state from browser
 * storage and updates browser storage when workbench state changes.  Updates workbench
 * state from `sessionId` and `groupId` URL parameters. Sets up auto scroll.
 * @returns boolean, true if finished initializing
 */
export function useInitializeWorkbench({
    systemId,
    locationId,
    saveUpdatesToBrowserStorage,
    updateStateFromUrl,
    additionalLoadActions,
}: WorkbenchInitializationOptions): boolean {
    const {
        autoScroll,
        range,
        zoomRange,
        setTimeRange,
        updateAutoScroll,
        updateFromBrowserStorage,
        isInitialized,
        setIsInitialized,
        stateInitializationFns: _stateInitializationFns,
    } = useWorkbenchValues((state) => ({
        autoScroll: state.autoScroll,
        range: state.range,
        zoomRange: state.zoomRange,
        setTimeRange: state.setTimeRange,
        updateAutoScroll: state.updateAutoScroll,
        updateFromBrowserStorage: state.updateFromBrowserStorage,
        isInitialized: state.isInitialized,
        setIsInitialized: (state as _WorkbenchStateInternal).setIsInitialized,
        stateInitializationFns: extractWorkbenchStateInitializerMethods(state),
    }));
    const setIsInitializedRef = useRef(setIsInitialized);
    setIsInitializedRef.current = setIsInitialized;

    const [loadedFromLocalStorage, setLoadedFromLocalStorage] = useState(isInitialized);
    const [loadedFromElsewhere, setLoadedFromElsewhere] = useState(isInitialized);

    const stateInitializationFunctionsRef = useRef(_stateInitializationFns);

    useInterval(
        updateAutoScroll,
        autoScroll && !isZoomedIn(range, zoomRange) ? AutoScrollUpdateMs : false,
        true
    );

    const workbenchStore = useZustandStore(WorkbenchContext);

    const email = useUser().user?.Email;

    const saveWorkbenchState = useCallback(() => {
        if (email) {
            // Don't store isInitialized
            const { isInitialized: _, ...state } = workbenchStore.getState();
            const stateWithSystem = { ...state, systemId };
            saveWorkbenchStateToBrowserStorage(email, stateWithSystem);
        }
    }, [email, systemId, workbenchStore]);

    const queryParams = useQueryParams();
    const trendQueryParams = useMemo<TrendQueryParams>(
        () => extractTrendUrlParams(queryParams),
        [queryParams]
    );

    const saveWorkbenchStateDebounced = useDebounceCallback(
        BrowserStorageDebounceMs,
        saveWorkbenchState
    );

    // Initialize workbench state from existing local storage.
    useEffect(() => {
        if (email && !loadedFromLocalStorage) {
            updateFromBrowserStorage(email, systemId);
            setLoadedFromLocalStorage(true);
        }
    }, [email, loadedFromLocalStorage, systemId, updateFromBrowserStorage]);

    const { loadFromQueryParams } = useLoadWorkbenchStateFromParams({
        systemId,
    });

    useEffect(() => {
        if (!updateStateFromUrl) {
            setLoadedFromElsewhere(true);
            return;
        }
        loadFromQueryParams(trendQueryParams).finally(() => setLoadedFromElsewhere(true));
    }, [
        trendQueryParams,
        updateStateFromUrl,
        loadedFromElsewhere,
        setTimeRange,
        loadFromQueryParams,
    ]);

    // This section will execute each fn in `additionalLoadActions` as soon as
    // it is marked as loaded by the caller and the prerequisite steps (loading
    // from local storage, url, etc.) have executed.
    const operationStatesRef = useRef<(Promise<unknown> | 'not-started')[]>(
        Array(additionalLoadActions?.length ?? 0).fill('not-started')
    );
    const initializePendingRef = useRef(false);
    useEffect(() => {
        if (loadedFromLocalStorage && loadedFromElsewhere && !isInitialized) {
            if (!additionalLoadActions) {
                setIsInitialized(true);
                return;
            }

            const promises = additionalLoadActions.map((op, idx) => {
                const fn = typeof op === 'function' ? op : op.fn;

                if (typeof op !== 'function' && op.isLoading) {
                    return 'not-started';
                }

                const curState = operationStatesRef.current[idx];
                return curState === 'not-started'
                    ? fn(stateInitializationFunctionsRef.current)
                    : curState;
            });

            if (promises.includes('not-started')) {
                return;
            }

            if (!initializePendingRef.current) {
                initializePendingRef.current = true;
                Promise.all(promises).then(() => {
                    setIsInitializedRef.current(true);
                });
            }
        }
    }, [
        loadedFromElsewhere,
        loadedFromLocalStorage,
        setIsInitialized,
        additionalLoadActions,
        isInitialized,
    ]);

    const isLoadingTimeRanges = useDefaultTimeRange({ locationId });

    // Configure saving workbench state to local storage.
    useEffect(() => {
        if (!email || !saveUpdatesToBrowserStorage) {
            return undefined;
        }

        updateFromBrowserStorage(email, systemId);
        window.addEventListener('focus', saveWorkbenchState);
        const unsubscribe = workbenchStore.subscribe(saveWorkbenchStateDebounced);

        return () => {
            window.removeEventListener('focus', saveWorkbenchState);
            unsubscribe();
        };
    }, [
        email,
        saveUpdatesToBrowserStorage,
        saveWorkbenchState,
        saveWorkbenchStateDebounced,
        systemId,
        updateFromBrowserStorage,
        workbenchStore,
    ]);

    return isInitialized && !isLoadingTimeRanges;
}
